import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase, isConfigured } from '../lib/supabase.js'
import { useToast } from '../context/ToastContext.jsx'

// Templates the admin can edit, with the placeholders each one supports and
// sample values used for the live preview.
const TEMPLATES = [
  {
    key: 'welcome',
    label: 'Welcome email',
    desc: 'Sent once to each new account that did NOT opt into offers (within ~1 min of signup). Keep this one promo-free.',
    vars: ['{{name}}', '{{site_url}}', '{{settings_url}}'],
    sample: { name: 'Alex', site_url: 'https://calculatemypeptides.com', settings_url: 'https://calculatemypeptides.com/settings' },
  },
  {
    key: 'welcome_optin',
    label: 'Welcome email — opted into offers',
    desc: 'Sent instead of the standard welcome to new accounts who ticked the marketing opt-in. This is the only welcome that may contain a sponsored offer — label it “Sponsored” and keep the physical address + preferences link.',
    vars: ['{{name}}', '{{site_url}}', '{{settings_url}}'],
    sample: { name: 'Alex', site_url: 'https://calculatemypeptides.com', settings_url: 'https://calculatemypeptides.com/settings' },
  },
  {
    key: 'reminder',
    label: 'Dose reminder email',
    desc: 'Sent to users who enabled reminders, before their scheduled doses.',
    vars: ['{{name}}', '{{count}}', '{{date}}', '{{doses}}', '{{calendar_url}}', '{{unsubscribe_url}}'],
    sample: {
      name: 'Alex', count: 2, date: '2026-08-20',
      doses: '<li><strong>BPC-157</strong> — 10 units at 8:00 AM</li><li><strong>TB-500</strong> — 20 units</li>',
      calendar_url: 'https://calculatemypeptides.com/calendar',
      unsubscribe_url: 'https://calculatemypeptides.com/unsubscribe?token=demo',
    },
  },
]

const render = (str, vars) => String(str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (k in vars ? String(vars[k]) : ''))

export default function AdminEmails() {
  const toast = useToast()
  const [rows, setRows] = useState({}) // key -> { subject, body_html }
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState(null)

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return }
    supabase.from('email_templates').select('key, subject, body_html').then(({ data }) => {
      const map = {}
      for (const r of data || []) map[r.key] = { subject: r.subject, body_html: r.body_html }
      setRows(map)
      setLoading(false)
    })
  }, [])

  const set = (key, field) => (e) => setRows((s) => ({ ...s, [key]: { ...s[key], [field]: e.target.value } }))

  const save = async (key) => {
    const row = rows[key]
    if (!row?.subject?.trim() || !row?.body_html?.trim()) return toast('Subject and body are required', 'red')
    setSavingKey(key)
    const { error } = await supabase.from('email_templates')
      .upsert({ key, subject: row.subject, body_html: row.body_html, updated_at: new Date().toISOString() })
    setSavingKey(null)
    if (error) return toast(error.message, 'red')
    toast('Template saved', 'green')
  }

  return (
    <main className="page">
      <div className="container page-narrow">
        <Link to="/admin" className="pep-back">← Site Admin</Link>
        <h1 className="section-title">Email Templates</h1>
        <p className="section-sub" style={{ maxWidth: '62ch' }}>
          Edit the welcome and dosing-reminder emails. Changes save to the database and the next
          cron run uses them — no deploy needed. (Password-reset emails are edited in Supabase.)
        </p>

        {!isConfigured && <div className="config-notice" style={{ marginBottom: 18 }}><strong>Backend not connected.</strong></div>}
        {loading && isConfigured && <div className="card empty"><div className="spinner" /></div>}

        {!loading && TEMPLATES.map((t) => {
          const row = rows[t.key] || { subject: '', body_html: '' }
          return (
            <section key={t.key} className="card card-pad" style={{ marginBottom: 22 }}>
              <h2 style={{ marginTop: 0, fontSize: '1.15rem' }}>{t.label}</h2>
              <p className="muted small" style={{ marginTop: 0 }}>{t.desc}</p>

              <div className="field">
                <label className="label">Subject</label>
                <input className="input" value={row.subject || ''} onChange={set(t.key, 'subject')} />
              </div>
              <div className="field">
                <label className="label">Body (HTML)</label>
                <textarea className="input embed-textarea" rows={10} value={row.body_html || ''} onChange={set(t.key, 'body_html')} />
              </div>
              <p className="muted small">
                Variables: {t.vars.map((v) => <code key={v} style={{ marginRight: 8 }}>{v}</code>)}
              </p>

              <div className="field">
                <label className="label">Live preview</label>
                <div className="card card-pad" style={{ background: '#fff', color: '#14304a' }}
                  dangerouslySetInnerHTML={{ __html: render(row.body_html, t.sample) }} />
              </div>

              <button className="btn btn-primary" disabled={savingKey === t.key} onClick={() => save(t.key)}>
                {savingKey === t.key ? 'Saving…' : `Save ${t.label.toLowerCase()}`}
              </button>
            </section>
          )
        })}
      </div>
    </main>
  )
}
